/**
 * Model outline extraction (EAA-1642).
 *
 * The editor needs two lists before it can offer any interaction authoring:
 * the animation clips a GLB ships with, and the objects a learner could click.
 * Both are read straight off the parsed scene graph — nothing is inferred from
 * the file name or the asset record.
 *
 * Extraction runs in the EDITOR, and the result is stored on the asset so the
 * player never re-derives it. Right after an upload we already hold the File,
 * so we parse those bytes locally instead of paying for a serve token plus a
 * second download of a file that can be 50-100MB.
 *
 * Objects are emitted as PATHS, not bare names — see extractObjectPaths.
 */

import type { Group, AnimationClip, Object3D } from 'three';

/** Separator between path segments. glTF names may contain almost anything else. */
export const OBJECT_PATH_SEP = '/';

export interface Model3DOutline {
    /** Names of the embedded AnimationClips, in file order. */
    animations: string[];
    /** Hierarchical paths of the objects a learner can click (see extractObjectPaths). */
    objects: string[];
}

/** Does this subtree contain any renderable geometry? */
function hasGeometry(node: Object3D): boolean {
    let found = false;
    node.traverse((child) => {
        if (found) return;
        if ((child as { isMesh?: boolean }).isMesh) found = true;
    });
    return found;
}

/**
 * Collect clickable objects as `Parent/Child/Grandchild` paths.
 *
 * Why paths rather than a flat list of names — the first cut emitted every
 * named node as a bare name, and on a real model that is unusable: the
 * reference "Ecosystem" scene produced 465 undifferentiated rows like
 * `Mesh_0001` and `Plane25`, with no way to tell which was which, and no way
 * to say "the whole fish" rather than one of its meshes.
 *
 * A path keeps the hierarchy the author already understands. The editor shows
 * the top level and lets them drill in, so a figure made of a torso, arms and
 * legs is ONE row until they ask for more; the player resolves a click to the
 * deepest mapped path, so mapping the parent covers every part of it.
 *
 * Rules:
 * - A node qualifies when it is named and has geometry somewhere beneath it.
 *   An empty pivot or a light is never a click target.
 * - Unnamed nodes are transparent: their children attach to the nearest named
 *   ancestor, so exporter scaffolding does not become a level of the tree.
 * - Duplicate paths are dropped; duplicate NAMES at different paths are kept
 *   apart, which bare names could not do.
 */
export function extractObjectPaths(root: Group): string[] {
    const paths: string[] = [];
    const seen = new Set<string>();

    const visit = (node: Object3D, prefix: string): void => {
        node.children.forEach((child) => {
            if (!hasGeometry(child)) return;
            const name = (child.name ?? '').trim();
            if (!name) {
                visit(child, prefix); // transparent: hoist its children to this level
                return;
            }
            const path = prefix ? `${prefix}${OBJECT_PATH_SEP}${name}` : name;
            if (!seen.has(path)) {
                seen.add(path);
                paths.push(path);
            }
            visit(child, path);
        });
    };

    visit(root, '');
    return paths;
}

/** The leaf name of an object path — what the editor shows as the row label. */
export function objectPathLeaf(path: string): string {
    const i = path.lastIndexOf(OBJECT_PATH_SEP);
    return i === -1 ? path : path.slice(i + 1);
}

/** The parent path, or '' for a top-level object. */
export function objectPathParent(path: string): string {
    const i = path.lastIndexOf(OBJECT_PATH_SEP);
    return i === -1 ? '' : path.slice(0, i);
}

/**
 * Find the object a path refers to, mirroring extractObjectPaths exactly.
 *
 * Used by the preview to resolve an editor selection back to a live node. The
 * same geometry filter and the same transparency of unnamed nodes apply, or a
 * path produced by extraction could fail to resolve here.
 */
export function findObjectByPath(root: Object3D, path: string): Object3D | null {
    if (!path) return null;
    let match: Object3D | null = null;

    const visit = (node: Object3D, prefix: string): void => {
        if (match) return;
        for (const child of node.children) {
            if (match) return;
            if (!hasGeometry(child)) continue;
            const name = (child.name ?? '').trim();
            if (!name) {
                visit(child, prefix); // transparent, exactly as in extraction
                continue;
            }
            const childPath = prefix ? `${prefix}${OBJECT_PATH_SEP}${name}` : name;
            if (childPath === path) {
                match = child;
                return;
            }
            // Only descend where the answer can be — a big scene is deep.
            if (path.startsWith(`${childPath}${OBJECT_PATH_SEP}`)) visit(child, childPath);
        }
    };

    visit(root, '');
    if (match) return match;

    // EAA-1642: fall back to the leaf name anywhere in the tree. A path can
    // fail to resolve when the stored outline was written against a different
    // build of the model (or by the earlier bare-name format), and silently
    // highlighting nothing is the worst possible answer — the author cannot
    // tell a failed lookup from an object they simply cannot see.
    const leaf = objectPathLeaf(path);
    let byName: Object3D | null = null;
    root.traverse((node) => {
        if (byName) return;
        if ((node.name ?? '').trim() === leaf && hasGeometry(node)) byName = node;
    });
    return byName;
}

export function extractModelOutline(root: Group, animations: AnimationClip[]): Model3DOutline {
    return {
        animations: animations.map((clip, i) => clip.name || `Animation ${i + 1}`),
        objects: extractObjectPaths(root),
    };
}

/**
 * Parse a just-picked .glb/.gltf File in the browser and read its outline.
 *
 * Used by the editor immediately after upload — no network round trip, and the
 * scene is thrown away as soon as the names are out.
 */
export async function extractOutlineFromFile(file: File): Promise<Model3DOutline> {
    const bytes = await file.arrayBuffer();
    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
    const loader = new GLTFLoader();
    return new Promise<Model3DOutline>((resolve, reject) => {
        loader.parse(
            bytes,
            '',
            (gltf) => resolve(extractModelOutline(gltf.scene, gltf.animations ?? [])),
            (err) => reject(err instanceof Error ? err : new Error('Failed to parse 3D model')),
        );
    });
}
